import merge from 'lodash/merge';
import { Stack } from '@mui/material';
import { zodResolver } from '@hookform/resolvers/zod';
import { FieldValues, useForm, FormProvider } from 'react-hook-form';
import { useCallback, useEffect, useMemo, useState, type PropsWithChildren } from 'react';

import { usePageTitle } from '@enroller/utils';
import { useHeaderContext, ValidationType } from '../../../contexts/HeaderContext';
import { useApplicationContext } from '../../../contexts/ApplicationContext/ApplicationContext';

import { defaultValues, type HousingFormValues, housingSchema, createHousingPayload } from './HousingFormValues';
import { HousingSection } from './HousingSection';
import { PetsSection } from './PetsSection';
import { PlacementSection } from './PlacementSection';
import { FoodSection } from './FoodSection';
import { AllergiesSection } from './AllergiesSection';
import type { StudentApplicationValues } from '../StudentApplication';

interface HousingFormProps {
  modulesID?: StudentApplicationValues['modulesID'];
  housingData?: HousingFormValues;
  submitApplication: (data: FieldValues) => Promise<void>;
}

export const HousingForm = (housingFormProps: HousingFormProps) => {
  return (
    <PageContainer {...housingFormProps}>
      <HousingSection />
      <PetsSection />
      <PlacementSection />
      <FoodSection />
      <AllergiesSection />
    </PageContainer>
  );
};

const PageContainer = ({ children, submitApplication, housingData, modulesID }: PropsWithChildren<HousingFormProps>) => {
  const { setPageTitle } = usePageTitle();
  const { setSubmit, setHeaderTitle, setIsFormLoading } = useHeaderContext();
  const [validationMode, setValidationMode] = useState<ValidationType | ''>('');
  const { setApplicationPageData, displayErrors } = useApplicationContext();

  useEffect(() => {
    setHeaderTitle({ title: 'Housing', subtitle: '' });
    setPageTitle('Housing');
  }, [setHeaderTitle, setPageTitle]);

  const resolver = useMemo(() => {
    const currentSchema = validationMode === ValidationType.Full ? housingSchema.required : housingSchema.optional;

    return zodResolver(currentSchema);
  }, [validationMode]);

  const methods = useForm({
    defaultValues,
    resolver,
  });

  const { reset, handleSubmit, trigger } = methods;

  // Trigger errors if they have been triggered in another form
  useEffect(() => {
    if (displayErrors) setValidationMode(ValidationType.Full);
  }, [displayErrors, trigger]);

  useEffect(() => {
    if (housingData) {
      reset(merge({}, defaultValues, housingData));
    }
  }, [housingData, reset]);

  const onSubmit = useCallback(
    () =>
      handleSubmit((data: HousingFormValues) => {
        setApplicationPageData('housing', data);

        if (!modulesID) return;
        setIsFormLoading(true);

        submitApplication(createHousingPayload(data, modulesID))
          .then((r) => console.log('[SAP] Housing API: submit success', r))
          .catch((e) => console.error('[SAP] Housing API: submit error:', e))
          .finally(() => setIsFormLoading(false));
      })(),
    [handleSubmit, setApplicationPageData, modulesID, setIsFormLoading, submitApplication],
  );

  // Submit form when resolver is changed.
  useEffect(() => {
    if (validationMode) {
      onSubmit();
      setValidationMode('');
    }
  }, [resolver, validationMode, onSubmit]);

  useEffect(() => {
    setSubmit(setValidationMode);
  }, [setSubmit, setValidationMode]);

  return (
    <FormProvider {...methods}>
      <Stack component="form" onSubmit={onSubmit} gap={2}>
        {children}
      </Stack>
    </FormProvider>
  );
};
